/**
 * PlanEditorModal - Create/Edit billing plan modal.
 * 
 * Features:
 * - Zod validation (mirrors backend)
 * - Feature checkbox selection
 * - Limit configuration
 * - Real-time validation feedback
 * 
 * @accessibility WCAG 2.1 AA (focus trap, ARIA, keyboard nav)
 * @i18n Ready for translation
 */
import React, { useState, useCallback, useEffect, useRef } from 'react'
import { X, Save, AlertTriangle, Info, Loader2 } from 'lucide-react'
import { z } from 'zod'
import { useAdminPlans, AdminPlan, PlanCreateData, PlanUpdateData, ALLOWED_FEATURES, RESERVED_SLUGS } from '../../hooks/useAdminPlans'

// =============================================================================
// Validation Schema (mirrors backend)
// =============================================================================

const SLUG_REGEX = /^[a-z0-9]+(?:-[a-z0-9]+)*$/

const planSchema = z.object({
  name: z.string().min(2, 'Minimum 2 caractères').max(100, 'Maximum 100 caractères'),
  slug: z.string()
    .min(2, 'Minimum 2 caractères')
    .max(50, 'Maximum 50 caractères')
    .regex(SLUG_REGEX, 'Slug invalide (lettres minuscules, chiffres, tirets)')
    .refine(s => !RESERVED_SLUGS.includes(s as any), 'Slug réservé'),
  description: z.string().max(1000, 'Maximum 1000 caractères').optional(),
  price_monthly: z.number().min(0, 'Prix minimum: 0').max(10000, 'Prix maximum: 10000€'),
  price_yearly: z.number().min(0).max(120000).optional(),
  currency: z.string().default('EUR'),
  features: z.array(z.string()),
  max_agents: z.number().int().min(-1),
  max_conversations_per_month: z.number().int().min(-1),
  max_messages_per_month: z.number().int().min(-1),
  max_knowledge_items: z.number().int().min(-1),
  max_api_calls_per_month: z.number().int().min(-1),
  max_file_size_mb: z.number().int().min(1).max(10000),
  max_storage_mb: z.number().int().min(10).max(50000),
  max_crawled_pages: z.number().int().min(-1),
  // Phase 1: Session Capping
  session_max_messages: z.number().int().min(1).max(10000).optional().nullable(),
  session_timeout_minutes: z.number().int().min(1).max(10080).optional().nullable(),
  session_max_duration_hours: z.number().int().min(1).max(720).optional().nullable(),
  // Phase 1: Rate Limiting
  rate_limit_requests_per_minute: z.number().int().min(0).optional().nullable(),
  rate_limit_requests_per_hour: z.number().int().min(0).optional().nullable(),
  rate_limit_requests_per_day: z.number().int().min(0).optional().nullable(),
  trial_days: z.number().int().min(0).max(90),
  is_featured: z.boolean(),
  is_hidden: z.boolean(),
  sort_order: z.number().int().min(0),
}).refine(
  data => !data.price_yearly || data.price_yearly <= data.price_monthly * 12,
  { message: 'Le prix annuel doit être inférieur ou égal à 12x le prix mensuel', path: ['price_yearly'] }
)

type PlanFormData = z.infer<typeof planSchema>

// =============================================================================
// Feature Labels
// =============================================================================

const FEATURE_LABELS: Record<string, { label: string; description: string }> = {
  basic_chat: { label: 'Chat de base', description: 'Conversations avec l\'IA' },
  rag: { label: 'Base de connaissances', description: 'Upload et recherche de documents' },
  mcp_tools: { label: 'Outils MCP', description: 'Intégration d\'outils externes' },
  workflows: { label: 'Workflows', description: 'Automatisations personnalisées' },
  widget_embed: { label: 'Widget embarqué', description: 'Intégration sur site web' },
  whatsapp: { label: 'WhatsApp', description: 'Connexion WhatsApp Business' },
  api_access: { label: 'Accès API', description: 'API REST pour intégrations' },
  remove_branding: { label: 'Sans branding', description: 'Retirer le logo TotemAI' },
  custom_branding: { label: 'Branding personnalisé', description: 'Logo et couleurs custom' },
  priority_support: { label: 'Support prioritaire', description: 'Réponse sous 24h' },
  analytics: { label: 'Analytics', description: 'Statistiques avancées' },
  advanced_analytics: { label: 'Analytics avancées', description: 'Export et rapports' },
}

// =============================================================================
// Default Values
// =============================================================================

const DEFAULT_VALUES: PlanFormData = {
  name: '',
  slug: '',
  description: '',
  price_monthly: 0,
  price_yearly: undefined,
  currency: 'EUR',
  features: ['basic_chat'],
  max_agents: 1,
  max_conversations_per_month: 100,
  max_messages_per_month: 1000,
  max_knowledge_items: 10,
  max_api_calls_per_month: 1000,
  max_file_size_mb: 10,
  max_storage_mb: 100,
  max_crawled_pages: 10,
  session_max_messages: undefined,
  session_timeout_minutes: undefined,
  session_max_duration_hours: undefined,
  rate_limit_requests_per_minute: undefined,
  rate_limit_requests_per_hour: undefined,
  rate_limit_requests_per_day: undefined,
  trial_days: 0,
  is_featured: false,
  is_hidden: false,
  sort_order: 0,
}

// =============================================================================
// Component Props
// =============================================================================

interface PlanEditorModalProps {
  plan: AdminPlan | null  // null = create mode
  onClose: (saved: boolean) => void
}

// =============================================================================
// Component
// =============================================================================

export default function PlanEditorModal({ plan, onClose }: PlanEditorModalProps) {
  const formRef = useRef<HTMLFormElement>(null)
  const isEditMode = !!plan
  
  const { createPlan, updatePlan } = useAdminPlans()
  
  // Form state
  const [formData, setFormData] = useState<PlanFormData>(() => {
    if (plan) {
      return {
        name: plan.name,
        slug: plan.slug,
        description: plan.description || '',
        price_monthly: plan.price_monthly,
        price_yearly: plan.price_yearly || undefined,
        currency: plan.currency,
        features: plan.features || [],
        max_agents: plan.max_agents,
        max_conversations_per_month: plan.max_conversations_per_month,
        max_messages_per_month: plan.max_messages_per_month || 1000,
        max_knowledge_items: plan.max_knowledge_items,
        max_api_calls_per_month: plan.max_api_calls_per_month,
        max_file_size_mb: plan.max_file_size_mb || 10,
        max_storage_mb: plan.max_storage_mb || 100,
        max_crawled_pages: plan.max_crawled_pages || 10,
        session_max_messages: plan.session_max_messages || undefined,
        session_timeout_minutes: plan.session_timeout_minutes || undefined,
        session_max_duration_hours: plan.session_max_duration_hours || undefined,
        rate_limit_requests_per_minute: plan.rate_limit_requests_per_minute || undefined,
        rate_limit_requests_per_hour: plan.rate_limit_requests_per_hour || undefined,
        rate_limit_requests_per_day: plan.rate_limit_requests_per_day || undefined,
        trial_days: plan.trial_days,
        is_featured: plan.is_featured,
        is_hidden: plan.is_hidden,
        sort_order: plan.sort_order,
      }
    }
    return DEFAULT_VALUES
  })
  
  const [errors, setErrors] = useState<Record<string, string>>({})
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState<string | null>(null)

  // Auto-generate slug from name
  const generateSlug = useCallback((name: string) => {
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '')
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-|-$/g, '')
      .slice(0, 50)
  }, [])

  // Form handlers
  const handleChange = useCallback((field: keyof PlanFormData, value: any) => {
    setFormData(prev => {
      const next = { ...prev, [field]: value }
      
      // Auto-generate slug when name changes (only in create mode)
      if (field === 'name' && !isEditMode && !prev.slug) {
        next.slug = generateSlug(value)
      }
      
      return next
    })
    
    // Clear field error
    if (errors[field]) {
      setErrors(prev => {
        const next = { ...prev }
        delete next[field]
        return next
      })
    }
  }, [isEditMode, generateSlug, errors])

  const handleFeatureToggle = useCallback((feature: string) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.includes(feature)
        ? prev.features.filter(f => f !== feature)
        : [...prev.features, feature]
    }))
  }, [])

  // Validation
  const validate = useCallback((): boolean => {
    const result = planSchema.safeParse(formData)
    
    if (!result.success) {
      const fieldErrors: Record<string, string> = {}
      result.error.issues.forEach(issue => {
        const field = issue.path[0] as string
        if (!fieldErrors[field]) {
          fieldErrors[field] = issue.message
        }
      })
      setErrors(fieldErrors)
      return false
    }
    
    setErrors({})
    return true
  }, [formData])

  // Submit
  const handleSubmit = useCallback(async (e: React.FormEvent) => {
    e.preventDefault()
    
    if (!validate()) return
    
    setSaving(true)
    setSaveError(null)
    
    try {
      if (isEditMode && plan) {
        const updateData: PlanUpdateData = {
          name: formData.name,
          description: formData.description || undefined,
          price_monthly: formData.price_monthly,
          price_yearly: formData.price_yearly,
          features: formData.features,
          max_agents: formData.max_agents,
          max_conversations_per_month: formData.max_conversations_per_month,
          max_messages_per_month: formData.max_messages_per_month,
          max_knowledge_items: formData.max_knowledge_items,
          max_api_calls_per_month: formData.max_api_calls_per_month,
          max_file_size_mb: formData.max_file_size_mb,
          max_storage_mb: formData.max_storage_mb,
          max_crawled_pages: formData.max_crawled_pages,
          trial_days: formData.trial_days,
          is_featured: formData.is_featured,
          is_hidden: formData.is_hidden,
          sort_order: formData.sort_order,
        }
        await updatePlan(plan.id, updateData)
      } else {
        const createData: PlanCreateData = {
          ...formData,
          description: formData.description || undefined,
        }
        await createPlan(createData)
      }
      
      onClose(true)
    } catch (err: any) {
      setSaveError(err.message || 'Erreur lors de la sauvegarde')
    } finally {
      setSaving(false)
    }
  }, [formData, isEditMode, plan, validate, createPlan, updatePlan, onClose])

  // Focus trap + ESC to close
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose(false)
      }
    }
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [onClose])

  // ==========================================================================
  // Render
  // ==========================================================================

  return (
    <div 
      className="fixed inset-0 z-50 bg-zinc-950"
      role="dialog"
      aria-modal="true"
      aria-labelledby="plan-editor-title"
    >
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-4 border-b border-zinc-800 bg-zinc-900">
        <h2 id="plan-editor-title" className="text-lg font-semibold text-white">
          {isEditMode ? `Modifier: ${plan?.name}` : 'Nouveau Plan'}
        </h2>
        <div className="flex items-center gap-3">
          <button
            type="button"
            onClick={() => onClose(false)}
            className="px-4 py-2 rounded-lg border border-zinc-700 text-zinc-300 hover:bg-zinc-800 transition-colors"
          >
            Annuler
          </button>
          <button
            type="button"
            onClick={() => formRef.current?.requestSubmit()}
            className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-emerald-600 hover:bg-emerald-500 text-white font-medium transition-colors disabled:opacity-50"
            disabled={saving}
          >
            {saving ? (
              <>
                <Loader2 className="w-4 h-4 animate-spin" />
                Sauvegarde...
              </>
            ) : (
              <>
                <Save className="w-4 h-4" />
                {isEditMode ? 'Mettre à jour' : 'Créer'}
              </>
            )}
          </button>
        </div>
      </div>
      
      {/* Error banner */}
      {saveError && (
        <div className="mx-6 mt-4 p-3 rounded-lg bg-red-900/20 border border-red-800 text-red-300 flex items-center gap-2">
          <AlertTriangle className="w-5 h-5 flex-shrink-0" />
          <span>{saveError}</span>
        </div>
      )}
      
      {/* Body - 3 columns */}
      <form 
        ref={formRef}
        onSubmit={handleSubmit}
        className="h-[calc(100vh-73px)] p-6 overflow-y-auto"
      >
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 h-full">
          
          {/* Column 1: Basic Info + Pricing */}
          <div className="space-y-6">
            {/* Basic Info */}
            <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
              <h3 className="text-sm font-medium text-zinc-400 uppercase tracking-wider mb-4">
                Informations générales
              </h3>
              
              <div className="space-y-4">
                {/* Name */}
                <div>
                  <label htmlFor="plan-name" className="block text-sm font-medium text-zinc-300 mb-1">
                    Nom <span className="text-red-400">*</span>
                  </label>
                  <input
                    id="plan-name"
                    type="text"
                    value={formData.name}
                    onChange={e => handleChange('name', e.target.value)}
                    className={`w-full px-3 py-2 bg-zinc-800 border rounded-lg text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 ${
                      errors.name ? 'border-red-500' : 'border-zinc-700'
                    }`}
                    placeholder="Pro Plan"
                  />
                  {errors.name && <p className="mt-1 text-sm text-red-400">{errors.name}</p>}
                </div>
                
                {/* Slug */}
                <div>
                  <label htmlFor="plan-slug" className="block text-sm font-medium text-zinc-300 mb-1">
                    Slug <span className="text-red-400">*</span>
                  </label>
                  <input
                    id="plan-slug"
                    type="text"
                    value={formData.slug}
                    onChange={e => handleChange('slug', e.target.value.toLowerCase())}
                    disabled={isEditMode}
                    className={`w-full px-3 py-2 bg-zinc-800 border rounded-lg text-zinc-100 font-mono focus:outline-none focus:ring-2 focus:ring-emerald-500/50 ${
                      errors.slug ? 'border-red-500' : 'border-zinc-700'
                    } ${isEditMode ? 'opacity-50 cursor-not-allowed' : ''}`}
                    placeholder="pro-plan"
                  />
                  {errors.slug && <p className="mt-1 text-sm text-red-400">{errors.slug}</p>}
                </div>
                
                {/* Description */}
                <div>
                  <label htmlFor="plan-description" className="block text-sm font-medium text-zinc-300 mb-1">
                    Description
                  </label>
                  <textarea
                    id="plan-description"
                    value={formData.description}
                    onChange={e => handleChange('description', e.target.value)}
                    rows={2}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/50 resize-none"
                    placeholder="Description courte..."
                  />
                </div>
              </div>
            </section>
            
            {/* Pricing */}
            <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-5">
              <h3 className="text-sm font-medium text-zinc-400 uppercase tracking-wider mb-4">
                Tarification
              </h3>
              
              <div className="grid grid-cols-2 gap-3">
                {/* Monthly */}
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-1">Mensuel (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price_monthly}
                    onChange={e => handleChange('price_monthly', parseFloat(e.target.value) || 0)}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                  />
                </div>
                {/* Yearly */}
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-1">Annuel (€)</label>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={formData.price_yearly || ''}
                    onChange={e => handleChange('price_yearly', e.target.value ? parseFloat(e.target.value) : undefined)}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                    placeholder="Optionnel"
                  />
                </div>
                {/* Trial */}
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-1">Jours d'essai</label>
                  <input
                    type="number"
                    min="0"
                    max="90"
                    value={formData.trial_days}
                    onChange={e => handleChange('trial_days', parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                  />
                </div>
                {/* Sort order */}
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-1">Ordre d'affichage</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.sort_order}
                    onChange={e => handleChange('sort_order', parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                  />
                </div>
              </div>
              
              {/* Options */}
              <div className="mt-4 flex flex-wrap gap-4">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_featured}
                    onChange={e => handleChange('is_featured', e.target.checked)}
                    className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-emerald-500"
                  />
                  <span className="text-sm text-zinc-300">Mis en avant</span>
                </label>
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={formData.is_hidden}
                    onChange={e => handleChange('is_hidden', e.target.checked)}
                    className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-emerald-500"
                  />
                  <span className="text-sm text-zinc-300">Masqué</span>
                </label>
              </div>
            </section>
          </div>
          
          {/* Column 2: Features */}
          <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 flex flex-col max-h-[calc(100vh-120px)]">
            <h3 className="text-sm font-medium text-zinc-400 uppercase tracking-wider mb-4 flex-shrink-0">
              Fonctionnalités
            </h3>
            
            <div className="grid grid-cols-1 gap-2 overflow-y-auto flex-1 pr-2">
              {ALLOWED_FEATURES.map(feature => {
                const info = FEATURE_LABELS[feature] || { label: feature, description: '' }
                const checked = formData.features.includes(feature)
                
                return (
                  <label
                    key={feature}
                    className={`flex items-center gap-3 p-2.5 rounded-lg border cursor-pointer transition-colors ${
                      checked 
                        ? 'bg-emerald-900/20 border-emerald-700' 
                        : 'bg-zinc-800/50 border-zinc-700 hover:border-zinc-600'
                    }`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      onChange={() => handleFeatureToggle(feature)}
                      className="w-4 h-4 rounded border-zinc-600 bg-zinc-800 text-emerald-500"
                    />
                    <div className="flex-1 min-w-0">
                      <div className="text-sm font-medium text-zinc-200">{info.label}</div>
                      <div className="text-xs text-zinc-500 truncate">{info.description}</div>
                    </div>
                  </label>
                )
              })}
            </div>
          </section>
          
          {/* Column 3: Limits */}
          <section className="bg-zinc-900 border border-zinc-800 rounded-xl p-5 h-fit">
            <h3 className="text-sm font-medium text-zinc-400 uppercase tracking-wider mb-1">
              Limites
            </h3>
            <p className="text-xs text-zinc-500 mb-4">-1 = illimité</p>
            
            <div className="grid grid-cols-2 gap-3">
              {[
                { key: 'max_agents', label: 'Agents' },
                { key: 'max_conversations_per_month', label: 'Conv/mois' },
                { key: 'max_messages_per_month', label: 'Messages/mois' },
                { key: 'max_knowledge_items', label: 'Documents' },
                { key: 'max_api_calls_per_month', label: 'API/mois' },
                { key: 'max_file_size_mb', label: 'Taille par fichier (MB)' },
                { key: 'max_storage_mb', label: 'Stockage (MB)' },
                { key: 'max_crawled_pages', label: 'Pages crawl' },
              ].map(({ key, label }) => (
                <div key={key}>
                  <label className="block text-sm font-medium text-zinc-300 mb-1">{label}</label>
                  <input
                    type="number"
                    min={key.includes('file_size') ? 1 : key.includes('storage') ? 10 : -1}
                    value={formData[key as keyof PlanFormData] as number}
                    onChange={e => handleChange(key as keyof PlanFormData, parseInt(e.target.value) || 0)}
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                  />
                </div>
              ))}
            </div>
            
            {/* Phase 1: Session Capping */}
            <div className="mt-4 pt-4 border-t border-zinc-700">
              <h4 className="text-xs font-medium text-zinc-400 uppercase tracking-wider mb-3">Session Capping</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-1">Max messages/session</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.session_max_messages || ''}
                    onChange={e => handleChange('session_max_messages', e.target.value ? parseInt(e.target.value) : null)}
                    placeholder="Ex: 20 (Free), 100 (Pro)"
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-1">Timeout (min)</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.session_timeout_minutes || ''}
                    onChange={e => handleChange('session_timeout_minutes', e.target.value ? parseInt(e.target.value) : null)}
                    placeholder="Ex: 30 (Free), 60 (Pro)"
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-zinc-300 mb-1">Durée max (heures)</label>
                  <input
                    type="number"
                    min="1"
                    value={formData.session_max_duration_hours || ''}
                    onChange={e => handleChange('session_max_duration_hours', e.target.value ? parseInt(e.target.value) : null)}
                    placeholder="Ex: 2 (Free), 8 (Pro), illimité (Enterprise)"
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                  />
                </div>
              </div>
            </div>
            
            {/* Phase 1: Rate Limiting */}
            <div className="mt-4 pt-4 border-t border-zinc-700">
              <h4 className="text-xs font-medium text-zinc-400 uppercase tracking-wider mb-3">Rate Limiting</h4>
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-1">Requêtes/minute</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.rate_limit_requests_per_minute || ''}
                    onChange={e => handleChange('rate_limit_requests_per_minute', e.target.value ? parseInt(e.target.value) : null)}
                    placeholder="Ex: 10 (Free), 60 (Pro)"
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-zinc-300 mb-1">Requêtes/heure</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.rate_limit_requests_per_hour || ''}
                    onChange={e => handleChange('rate_limit_requests_per_hour', e.target.value ? parseInt(e.target.value) : null)}
                    placeholder="Ex: 300 (Free), 3000 (Pro)"
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                  />
                </div>
                <div className="col-span-2">
                  <label className="block text-sm font-medium text-zinc-300 mb-1">Requêtes/jour</label>
                  <input
                    type="number"
                    min="0"
                    value={formData.rate_limit_requests_per_day || ''}
                    onChange={e => handleChange('rate_limit_requests_per_day', e.target.value ? parseInt(e.target.value) : null)}
                    placeholder="Ex: 5000 (Free), 50000 (Pro), illimité (Enterprise)"
                    className="w-full px-3 py-2 bg-zinc-800 border border-zinc-700 rounded-lg text-zinc-100 placeholder:text-zinc-500 focus:outline-none focus:ring-2 focus:ring-emerald-500/50"
                  />
                </div>
              </div>
            </div>
          </section>
          
        </div>
      </form>
    </div>
  )
}
